import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { ChevronDown, Loader2, Save, Globe } from "lucide-react";
import { getDocData, setDocData } from "../../Firebase/Firebase.utils";
import { FormSection, TextField, TextArea } from "./FormShell";
import FileDropzone from "./FileDropzone";
import { revalidatePublic } from "../utils/revalidatePublic";

const META_TITLE_MAX = 60;
const META_DESC_MAX = 160;
const SETTINGS_COLLECTION = "pageSettings";

const EMPTY_FORM = {
  heading: "",
  subheading: "",
  heroImage: "",
  metaTitle: "",
  metaDescription: "",
};

export default function PublicPageSettings({
  collectionKey,
  publicPath,
  defaultHeading,
  defaultSubheading,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [initial, setInitial] = useState(EMPTY_FORM);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const data = await getDocData(SETTINGS_COLLECTION, collectionKey);
      if (!active) return;
      const next = {
        heading: data?.heading ?? defaultHeading ?? "",
        subheading: data?.subheading ?? defaultSubheading ?? "",
        heroImage: data?.heroImage ?? "",
        metaTitle: data?.metaTitle ?? "",
        metaDescription: data?.metaDescription ?? "",
      };
      setForm(next);
      setInitial(next);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [collectionKey, defaultHeading, defaultSubheading]);

  const isDirty = Object.keys(EMPTY_FORM).some(
    (k) => form[k] !== initial[k]
  );

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const setHeroImage = (url) =>
    setForm((prev) => ({ ...prev, heroImage: url }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDocData(SETTINGS_COLLECTION, collectionKey, {
        ...form,
        updatedAt: new Date().toISOString(),
      });
      if (publicPath) revalidatePublic([publicPath]);
      setInitial(form);
      toast.success("Page settings saved.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save page settings.");
    } finally {
      setSaving(false);
    }
  };

  const titleLen = form.metaTitle.length;
  const descLen = form.metaDescription.length;
  const saveDisabled = !isDirty || saving || isUploading;

  return (
    <section className="mb-6 bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className="mt-0.5 w-8 h-8 rounded-lg bg-leechiuBlue/10 text-leechiuBlue flex items-center justify-center flex-shrink-0">
            <Globe className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-leechiuBlue">
              Public page settings
            </h3>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              Hero, heading, and SEO for{" "}
              <span className="font-mono text-[11px] text-gray-600">
                {publicPath}
              </span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {isDirty ? (
            <span className="text-[11px] font-medium text-leechiuOrange bg-leechiuOrange/10 px-2 py-0.5 rounded-full">
              Unsaved changes
            </span>
          ) : null}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {open ? (
        loading ? (
          <div className="px-5 pb-5 pt-1 flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="px-5 pb-5 pt-1 border-t border-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <FormSection
                title="Hero"
                description="Image, heading, and subheading shown at the top of the public page."
              >
                <FileDropzone
                  label="Hero image"
                  kind="image"
                  collection={SETTINGS_COLLECTION}
                  docId={collectionKey}
                  value={form.heroImage}
                  onChange={setHeroImage}
                  onUploadingChange={setIsUploading}
                />
                <TextField
                  label="Heading"
                  value={form.heading}
                  onChange={update("heading")}
                  placeholder="e.g. Blogs"
                  hint="The large title displayed over the hero image."
                />
                <TextArea
                  label="Subheading"
                  value={form.subheading}
                  onChange={update("subheading")}
                  rows={2}
                  placeholder="A short intro shown beneath the heading."
                />
              </FormSection>

              <FormSection
                title="SEO metadata"
                description="Used by search engines and social previews."
              >
                <div>
                  <TextField
                    label="Meta title"
                    value={form.metaTitle}
                    onChange={update("metaTitle")}
                    placeholder="Shown in browser tabs and Google results"
                    maxLength={META_TITLE_MAX + 20}
                  />
                  <CounterHint
                    length={titleLen}
                    max={META_TITLE_MAX}
                    recommendation="50–60 characters"
                  />
                </div>
                <div>
                  <TextArea
                    label="Meta description"
                    value={form.metaDescription}
                    onChange={update("metaDescription")}
                    rows={3}
                    placeholder="Brief summary shown under the title in search results"
                    maxLength={META_DESC_MAX + 40}
                  />
                  <CounterHint
                    length={descLen}
                    max={META_DESC_MAX}
                    recommendation="150–160 characters"
                  />
                </div>
              </FormSection>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              {isUploading ? (
                <span className="text-xs text-gray-500 mr-1">
                  Uploading image…
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => setForm(initial)}
                disabled={!isDirty || saving}
                className="px-4 py-2 text-sm rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saveDisabled}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-leechiuOrange text-white text-sm font-medium hover:opacity-90 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? "Saving…" : "Save settings"}
              </button>
            </div>
          </div>
        )
      ) : null}
    </section>
  );
}

function CounterHint({ length, max, recommendation }) {
  const over = length > max;
  return (
    <div className="mt-1 flex items-center justify-between text-[11px]">
      <span className="text-gray-400">Recommended: {recommendation}</span>
      <span className={over ? "text-red-500 font-medium" : "text-gray-400"}>
        {length}/{max}
      </span>
    </div>
  );
}
